import { useCallback, useEffect, useRef, useState } from 'react';
import { holdPendingRequest, type HoldAction } from '../services/coApprovalDj';
import type { ReviewHold } from '../services/requests';
import { RENEW_THROTTLE_MS, holdWarning, type HoldWarning } from '../utils/reviewHold';

export type ClaimOutcome =
  | { status: 'claimed' }
  | { status: 'held'; heldBy: ReviewHold }
  | { status: 'error'; error: unknown };

interface UseReviewHoldOptions {
  eventId: string | undefined;
  /** The co-approval DJ's link token; omitted on the DJ's screens, which hold as the DJ. */
  token?: string;
  /** No hold calls at all when false (the DJ's screens with no active helper). */
  enabled: boolean;
  /** The hold ran out or someone else took the song; the host closes its pop-up. */
  onExpired: () => void;
}

interface HeldSong {
  eventId: string;
  requestId: string;
  untilMs: number;
}

/** For hosts that review songs from several events (the DJ's cross-event pop-up). */
export interface ClaimTarget {
  eventId: string;
  enabled: boolean;
}

// One reserved song at a time while its review pop-up is open (ITER-194)
export function useReviewHold({ eventId, token, enabled, onExpired }: UseReviewHoldOptions) {
  const [held, setHeld] = useState<HeldSong | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const heldRef = useRef<HeldSong | null>(null);
  const lastRenewRef = useRef(0);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  const setHeldSong = useCallback((next: HeldSong | null) => {
    heldRef.current = next;
    setHeld(next);
  }, []);

  const call = useCallback((songEventId: string, requestId: string, action: HoldAction) => {
    return holdPendingRequest({ eventId: songEventId, requestId, action, token });
  }, [token]);

  const release = useCallback(() => {
    const current = heldRef.current;
    if (!current) return;
    setHeldSong(null);
    call(current.eventId, current.requestId, 'release').catch(error =>
      console.warn('[useReviewHold] Release failed; the hold will expire on its own:', error));
  }, [call, setHeldSong]);

  const claim = useCallback(async (requestId: string, target?: ClaimTarget): Promise<ClaimOutcome> => {
    const songEventId = target?.eventId ?? eventId;
    if (heldRef.current && heldRef.current.requestId !== requestId) release();
    if (!(target?.enabled ?? enabled) || !songEventId) return { status: 'claimed' };
    try {
      const result = await call(songEventId, requestId, 'claim');
      if (!result.ok || !result.hold) {
        return result.heldBy ? { status: 'held', heldBy: result.heldBy } : { status: 'error', error: new Error('Hold refused') };
      }
      lastRenewRef.current = Date.now();
      setNowMs(Date.now());
      setHeldSong({ eventId: songEventId, requestId, untilMs: result.hold.untilMs });
      return { status: 'claimed' };
    } catch (error) {
      console.error('[useReviewHold] Claim failed:', error);
      return { status: 'error', error };
    }
  }, [eventId, enabled, call, release, setHeldSong]);

  const renew = useCallback(async (force = false) => {
    const current = heldRef.current;
    if (!current) return;
    if (!force && Date.now() - lastRenewRef.current < RENEW_THROTTLE_MS) return;
    lastRenewRef.current = Date.now();
    try {
      const result = await call(current.eventId, current.requestId, 'renew');
      if (heldRef.current?.requestId !== current.requestId) return;
      if (!result.ok || !result.hold) {
        setHeldSong(null);
        onExpiredRef.current();
        return;
      }
      setHeldSong({ ...current, untilMs: result.hold.untilMs });
    } catch (error) {
      console.warn('[useReviewHold] Renew failed; will retry on the next activity:', error);
      lastRenewRef.current = 0;
    }
  }, [call, setHeldSong]);

  useEffect(() => {
    if (!held) return;
    const interval = setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (heldRef.current && holdWarning(heldRef.current.untilMs, now) === 'expired') {
        setHeldSong(null);
        onExpiredRef.current();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [held, setHeldSong]);

  useEffect(() => {
    const handlePageHide = () => release();
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      release();
    };
  }, [release]);

  const warning: HoldWarning = held ? holdWarning(held.untilMs, nowMs) : 'none';
  const onActivity = useCallback(() => { void renew(); }, [renew]);

  return {
    claim,
    release,
    renew: () => renew(true),
    warning,
    heldRequestId: held?.requestId ?? null,
    activityHandlers: { onPointerDownCapture: onActivity, onKeyDownCapture: onActivity },
  };
}
